<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'Choosing AI Use Cases for GMP Quality Work — QikSolve',
  description:
    'A practical AI governance approach for GMP teams to assess which quality and operational problems are suitable for governed AI assistance.',
});

const page = {
  hero: {
    eyebrow: 'GMP AI use cases',
    title: 'Choose the quality problem before choosing the AI',
    summary:
      'The right starting point for AI governance is a defined quality or operational problem, with clear intended use, process impact, ownership, and evidence expectations.',
    cta: { label: 'Discuss your GMP AI pathway', to: '/contact' },
  },
  intro: {
    title: 'Start with the work that needs to improve',
    content:
      'AI is not automatically the right answer for every GMP process. A useful assessment begins with the work people do, the decision that needs support, the records involved, and the quality risks that must remain visible.',
    items: [
      { title: 'Define intended use', description: 'State what the AI-assisted workflow is meant to do and what remains outside its role.' },
      { title: 'Understand process impact', description: 'Identify whether the output informs a task, influences a quality decision, or enters a controlled record or approval.' },
      { title: 'Name accountable roles', description: 'Make ownership for the process, review, decision, and evidence explicit before a pilot begins.' },
    ],
  },
  sections: [
    {
      title: 'Questions for a governed pilot',
      items: [
        { title: 'What problem is being solved?', description: 'Describe the recurring quality or operational problem rather than starting with a preferred tool.' },
        { title: 'What evidence already exists?', description: 'Identify approved references, records, process measures, and review criteria that can support evaluation.' },
        { title: 'What could go wrong?', description: 'Consider inaccurate output, missing context, inconsistent review, data-integrity concerns, and unclear escalation.' },
        { title: 'What must remain human?', description: 'Define decisions, approvals, releases, and interpretations that require accountable human judgement.' },
      ],
    },
    {
      title: 'A proportionate next step',
      content:
        'A bounded use case with a clear owner and review path gives a GMP team something practical to evaluate. The next step may be process mapping, governance design, a controlled pilot, or a referral to QxAIOS for solution depth.',
      items: [
        { title: 'Map the workflow', description: 'Trace inputs, AI assistance, human review, decisions, records, and downstream actions.' },
        { title: 'Set the review boundary', description: 'Decide what evidence and approval are needed before the output can affect the process.' },
      ],
    },
  ],
  callout: {
    title: 'Good AI governance starts with a good quality question',
    content: 'QikSolve helps teams frame the use case and operating model. QxAIOS provides the solution and platform depth when that path is confirmed.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Continue the decision',
    items: [
      { label: 'Practical, governed AI hub', description: 'Return to the full Pillar 5 governance pathway.', to: '/product/agentic-ai-governance' },
      { label: 'Define the AI output boundary', description: 'Classify how an output affects a GMP process.', to: '/product/defining-gmp-boundary-for-ai-outputs' },
      { label: 'Explore QxAIOS', description: 'Visit the QxAIOS solution for platform and product detail.', to: 'https://qx.qiksolve.com' },
    ],
  },
  cta: { title: 'Have a GMP use case to work through?', content: 'Bring the process, decision, and evidence questions your team is carrying.', label: 'Discuss your pathway', to: '/contact' },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>
